import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import {
  counties,
  TIME_ZONE,
  orderSources,
  orderStatus,
  cancellationReasons,
  groupPurchaseStatus,
} from '@silvergatedelivery/constants';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Popover,
  Toolbar,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import DataTable from 'components/Table/DataTable';
import OrderDialog from 'components/Order/OrderDialog';
import {
  listOrders,
  getOrdersByCountyByDate,
  getOrdersByClientByDate,
  getOrdersByElderByDate,
  getOrdersByRestaurantByCreatedAt,
  getOrdersByCountyByStatus,
  getOrdersByCountyBySource,
  getOrdersByDeliveryStaffByDate,
  getOrdersByStatusByDate,
  getOrdersByRestaurantByDate,
  getRecurringOrderLogByRecurringOrderIdByDate,
  getOrder,
} from 'graphql/queries';
import OrderStatusChip from 'components/OrderStatusChip';
import NestedTableContainer from 'components/Table/NestedTableContainer';
import Order from 'components/Order/Order';
import LinkButton from 'components/Table/LinkButton';
import AdminOrderEditButton from 'forms/AdminOrderForm/AdminOrderEditButton';
import cache from 'utilities/cache';
import UpdateOrderToCompletedButton from 'components/Order/UpdateOrderToCompletedButton';
import UpdateOrderToCancelledButton from 'components/Order/UpdateOrderToCancelledButton';
import UpdateOrderDeliveryStaffButton from 'components/Order/UpdateOrderDeliveryStaffButton';
import UpdateOrderRestaurantButton from 'components/Order/UpdateOrderRestaurantButton';
import UpdateOrderDeliveryFeeButton from 'components/Order/UpdateOrderDeliveryFeeButton';
import UpdateOrderTimeButton from 'components/Order/UpdateOrderTimeButton';
import UpdateOrderDateButton from 'components/Order/UpdateOrderDateButton';
import UpdateOrderCategoryButton from 'components/Order/UpdateOrderCategoryButton';
import UpdateOrderLinkGroupEldersButton from 'components/Order/UpdateOrderLinkGroupEldersButton';
import ResetCancelledOrderButton from 'components/Order/ResetCancelledOrderButton';
import PlaceLalamoveMultiStopButton from 'components/Order/PlaceLalamoveMultiStopButton';
import { getTranslatedOrderStatus, getTranslatedMealSlots } from 'utilities/translate';
import { formatAddress, formatAddressLatLng, formatTags, getShortIDFromUUID } from 'utilities/format';
import { asyncListAll, request } from 'utilities/graph';
import Loading from 'components/Loading';
import deliveryStaffFeeCalculationMethod from '@silvergatedelivery/constants/src/deliveryStaffFeeCalculationMethod';
import { getDeliveryStaffIdSchema } from 'forms/schemas';
import { useCache } from 'CacheProvider';
import { sortBy } from 'utilities/sorting';
import ReceiptPrinterButton from 'views/FacilityAdmin/components/ReceiptPrinterButton';

const useStyles = makeStyles(() => ({
  popover: {
    pointerEvents: 'none',
  },
  paper: {
    padding: '10px',
    maxWidth: '400px',
    whiteSpace: 'pre-wrap',
    backgroundColor: 'rgba(229, 248, 191, 0.83)',
  },
}));

const lastWeek = moment().tz(TIME_ZONE).add(-7, 'days').format('YYYY-MM-DD');
const todayDate = moment().tz(TIME_ZONE).format('YYYY-MM-DD');
const nextWeek = moment().tz(TIME_ZONE).add(7, 'days').format('YYYY-MM-DD');

export default function OrderTable({
  title = '訂單',
  description,
  clientId,
  elderId,
  restaurantId,
  deliveryStaffId,
  recurringOrderId,
  queryStatus,
  defaultIndex = 0,
  orderIds: inOrderIds,
  showElderTags = false,
  ...props
}) {
  const classes = useStyles();
  const { t } = useTranslation();
  const [indexes, setIndexes] = useState();
  const [queryParams, setQueryParams] = useState();
  const [data, setData] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const ordersSelected = useRef([]);
  const [deliveryStaffOptions, setDeliveryStaffOptions] = useState();
  const { appGroup, selectedUserGroupParams } = useCache();
  const [dietaryNote, setDietaryNote] = useState({
    dialogOpen: false,
    popoverAnchorEl: null,
    content: '',
  });

  const isNotePopoverOpen = Boolean(dietaryNote.popoverAnchorEl);

  let defaultQueryOrderStatus = queryStatus || cache.get('app:query:orderStatus') || 'cancelled';
  if (defaultQueryOrderStatus === 'undefined') {
    defaultQueryOrderStatus = undefined;
  }

  const facilityMode = appGroup === 'FacilityAdmins';
  const clientData = selectedUserGroupParams?.clientData;

  const isEligibleForReceiptPrinter =
  facilityMode && clientData?.featureConfiguration?.isEligibleForReceiptPrinter === true;

  const isEligibleForGroupPurchase =
  facilityMode && clientData?.featureConfiguration?.isEligibleForGroupPurchase === true;

  const translatedOrderStatus = getTranslatedOrderStatus();
  const notAdminMode = appGroup !== 'Admins';
  const isAdminMode = appGroup === 'Admins';

  const defaultIndexes = [
    {
      name: 'getOrdersByCountyByDate',
      label: `按地區/${t('送餐')}時間/狀態查詢`,
      partitionKey: 'county',
      sortKey: 'date',
      fields: [{
        label: '縣市',
        key: 'county',
        type: 'select',
        options: counties.map((item) => {
          return { value: item, label: item };
        }),
      }, {
        label: `${t('送餐')}時間`,
        key: 'date',
        type: 'date',
      }, {
        label: '狀態',
        key: 'status',
        type: 'select',
        options: [{ value: undefined, label: '全部' }, ...translatedOrderStatus],
      }],
      operation: getOrdersByCountyByDate,
    },
    {
      name: 'getOrdersByCountyByDate',
      label: `按地區/${t('送餐')}時間/訂單來源查詢`,
      partitionKey: 'county',
      sortKey: 'date',
      fields: [{
        label: '縣市',
        key: 'county',
        type: 'select',
        options: counties.map((item) => {
          return { value: item, label: item };
        }),
      }, {
        label: `${t('送餐')}時間`,
        key: 'date',
        type: 'date',
      }, {
        label: '訂單來源',
        key: 'source',
        type: 'select',
        options: [{ value: undefined, label: '全部' }, ...orderSources],
      }],
      operation: getOrdersByCountyByDate,
    },
    {
      name: 'getOrdersByCountyByStatus',
      label: '按地區/狀態查詢',
      partitionKey: 'county',
      sortKey: 'status',
      fields: [{
        label: '縣市',
        key: 'county',
        type: 'select',
        options: counties.map((item) => {
          return { value: item, label: item };
        }),
      }, {
        label: '狀態',
        key: 'status',
        type: 'select',
        options: [{ value: undefined, label: '全部' }, ...translatedOrderStatus],
      }],
      operation: getOrdersByCountyByStatus,
    },
    {
      name: 'getOrdersByCountyBySource',
      label: '按地區/訂單來源查詢',
      partitionKey: 'county',
      sortKey: 'source',
      fields: [{
        label: '縣市',
        key: 'county',
        type: 'select',
        options: counties.map((item) => {
          return { value: item, label: item };
        }),
      }, {
        label: '訂單來源',
        key: 'source',
        type: 'select',
        options: [{ value: undefined, label: '全部' }, ...orderSources],
      }],
      operation: getOrdersByCountyBySource,
    },
    {
      name: 'getOrdersByStatusByDate',
      label: '按狀態查詢',
      partitionKey: 'status',
      sortKey: 'date',
      fields: [{
        label: '狀態',
        key: 'status',
        type: 'select',
        options: [{ value: undefined, label: '全部' }, ...translatedOrderStatus],
      }],
      operation: getOrdersByStatusByDate,
    },
    {
      name: 'scan',
      label: '掃描全部資料',
      fields: [{
        label: `${t('送餐')}時間`,
        key: 'date',
        type: 'date',
      }, {
        label: '狀態',
        key: 'status',
        type: 'select',
        options: [{ value: undefined, label: '全部' }, ...translatedOrderStatus],
      }],
      operation: listOrders,
    },
  ];

  const customToolbarSelect = ({ onUpdate }) =>{
    const buttons = [
      UpdateOrderToCompletedButton,
      UpdateOrderToCancelledButton,
      ResetCancelledOrderButton,
      UpdateOrderDeliveryStaffButton,
      UpdateOrderRestaurantButton,
      UpdateOrderTimeButton,
      UpdateOrderDateButton,
      UpdateOrderCategoryButton,
      UpdateOrderDeliveryFeeButton,
      UpdateOrderLinkGroupEldersButton,
    ];

    if (!facilityMode || (facilityMode && selectedUserGroupParams?.clientData?.featureConfiguration?.supportedPartners.includes('lalamove(multi-stop)'))) {
      buttons.push(PlaceLalamoveMultiStopButton);
    }

    return (
      <Toolbar style={{ display: 'flex', overflowX: 'scroll', gap: 2, whiteSpace: 'nowrap' }}>
        {buttons.map((ButtonComponent, idx) => (
          <div key={idx} style={{ minWidth: 'fit-content', flexShrink: 0 }}>
            <ButtonComponent ordersSelected={ordersSelected} onUpdate={onUpdate} />
          </div>
        ))}
      </Toolbar>);
  };

  const options = {
    expandableRows: true,
    isRowExpandable: () => true,
    renderExpandableRow(rowData) {
      const id = rowData[0];
      return (
        <NestedTableContainer columns={columns} noPadding={true}>
          <Order id={id} />
        </NestedTableContainer>
      );
    },
    selectableRows: 'multiple',
    isRowSelectable: () => true,
  };
  const translatedMealSlots = getTranslatedMealSlots();
  const columns = [
    {
      name: 'id',
      label: '編號',
      isTemplate: true,
      options: {
        display: false,
        filter: false,
        sort: true,
      },
    },
    {
      name: 'shortId',
      label: t('取餐編號'),
      options: {
        filter: false,
        sort: false,
        customBodyRender(_, tableMeta) {
          const id = tableMeta.rowData[columns.findIndex((col) => col.name === 'id')];
          return getShortIDFromUUID(id);
        },
      },
    },
    {
      name: 'referenceId',
      label: '整合編號',
      options: {
        display: false,
        filter: false,
        sort: true,
        customBodyRender(value) {
          return (value || '').split('-')[1] || '';
        },
      },
    },
    {
      name: 'county',
      label: '縣市',
      options: {
        // filterList: usePreFilter ? [cache.get('app:location')].filter((x) => x) : [],
        filter: true,
        sort: true,
      },
    },
    {
      name: 'clientId',
      label: '機構/客戶編號',
      options: {
        filter: false,
        sort: true,
        display: notAdminMode ? 'excluded' : false,
      },
    },
    {
      name: 'client.name',
      label: '機構名稱/客戶姓名',
      options: {
        filter: true,
        sort: true,
        display: notAdminMode ? 'excluded' : true,
      },
    },
    {
      name: 'tier',
      label: '目前層級',
      options: {
        filter: true,
        sort: true,
        display: false,
      },
    },
    {
      name: 'category',
      label: '類別',
      options: {
        filter: true,
        sort: true,
        display: false,
      },
    },
    {
      name: 'elderId',
      label: `${t('送餐對象')}編號`,
      options: {
        display: false,
        filter: false,
        sort: true,
      },
    },
    {
      name: 'elder.name',
      label: t('送餐對象'),
      options: {
        filter: false,
        sort: true,
      },
    },
    {
      name: 'elder.address',
      label: '送餐地址',
      options: {
        display: false,
        filter: false,
        sort: true,
        customBodyRender(value) {
          return formatAddress(value);
        },
        sortCompare(order) {
          return (obj1, obj2) => {
            const addr1 = formatAddress(obj1.data);
            const addr2 = formatAddress(obj2.data);
            if (addr1 === addr2) {
              return (obj1.rowData[0] + obj1.rowData[1] > obj2.rowData[0] + obj2.rowData[1] ? 1 : -1) * (order === 'asc' ? 1 : -1);
            }
            return (addr1 > addr2 ? 1 : -1) * (order === 'asc' ? 1 : -1);
          };
        },
      },
    },
    {
      name: 'elderLatLng',
      label: '送餐地址經緯度',
      options: {
        display: false,
        filter: false,
        sort: false,
        customBodyRender(_, tableMeta) {
          const address = tableMeta.rowData[columns.findIndex((col) => col.name === 'elder.address')];
          return formatAddressLatLng(address);
        },
      },
    },
    {
      name: 'elder.tags',
      label: '送餐對象標籤',
      options: {
        display: showElderTags ? true : 'excluded',
        filter: false,
        sort: false,
        customBodyRender: formatTags,
      },
    },
    {
      name: 'restaurant.name',
      label: t('餐廳'),
      options: {
        filter: false,
        sort: true,
      },
    },
    {
      name: 'deliveryStaffId',
      label: t('送餐大使'),
      options: {
        filter: false,
        sort: true,
        customBodyRender(deliveryStaffId) {
          if (deliveryStaffOptions) {
            const index = deliveryStaffOptions.enum.findIndex((id) => id === deliveryStaffId);
            if (index !== -1) {
              return deliveryStaffOptions.enumNames[index];
            }
          }
          return '';
        },
      },
    },
    {
      name: 'date',
      label: '日期',
      edit: {
        type: 'text',
      },
      options: {
        display: true,
        filter: true,
        sort: true,
      },
    },
    {
      name: 'week',
      label: '星期',
      edit: {
        type: 'text',
      },
      options: {
        display: false,
        filter: true,
        sort: true,
        customBodyRender(_, tableMeta) {
          const date = tableMeta.rowData[columns.findIndex((col) => col.name === 'date')];
          return moment(date).format('dddd');
        },
        sortCompare(order) {
          return (obj1, obj2) => {
            const date1 = moment(obj1.rowData[columns.findIndex((col) => col.name === 'date')]);
            const date2 = moment(obj2.rowData[columns.findIndex((col) => col.name === 'date')]);
            const day1 = date1.day() || 7; // 0 - 6, if 0, assign to 7 for Sunday
            const day2 = date2.day() || 7;
            return (day1 > day2 ? 1 : -1) * (order === 'asc' ? 1 : -1);
          };
        },
      },
    },
    {
      name: 'deliveryBy',
      label: '時間',
      type: 'time',
      options: {
        display: true,
        filter: false,
        sort: true,
      },
    },
    {
      name: 'mealSlot',
      label: '時段',
      edit: {
        type: 'text',
      },
      options: {
        filter: true,
        sort: true,
        customBodyRender(value) {
          return translatedMealSlots.find((item) => item.value === value).label;
        },
      },
    },
    {
      name: 'status',
      label: '狀態',
      edit: {
        type: 'text',
      },
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => {
          return (
            <OrderStatusChip status={value} />
          );
        },
        filterOptions: {
          renderValue: (v) => (orderStatus.find(({ value }) => value === v) || {}).label,
        },
        customFilterListOptions: {
          render: (v) => (orderStatus.find(({ value }) => value === v) || {}).label,
        },
      },
    },
    {
      name: 'groupPurchaseStatus',
      label: '團購狀態',
      options: {
        display: isEligibleForGroupPurchase ? true : 'excluded',
        filter: true,
        sort: true,
        customBodyRender: (v) => {
          return (groupPurchaseStatus.find(({ value }) => value === v) || {}).label;
        },
      },
    },
    {
      name: 'cancellationReason',
      label: '訂單取消原因',
      options: {
        display: false,
        filter: true,
        sort: true,
        customBodyRender: (value, tableMeta, updateValue) => {
          const reason = cancellationReasons.find((reason) => reason.value === value);
          if (reason) {
            return reason.label;
          }
          return '';
        },
      },
    },
    {
      name: 'cancellationNote',
      label: '訂單取消備註',
      options: {
        display: false,
        filter: false,
        sort: true,
      },
    },
    {
      name: 'direction',
      label: '距離',
      options: {
        filter: false,
        sort: true,
        customBodyRender(value) {
          return value.distance;
        },
        sortCompare(order) {
          return ({ data: value1 }, { data: value2 }) => {
            return (value1.distanceInMeters > value2.distanceInMeters ? 1 : -1) * (order === 'asc' ? 1 : -1);
          };
        },
      },
    },
    {
      name: 'noteForDelivery',
      label: `${t('送餐')}備註`,
      options: {
        display: false,
        filter: false,
        sort: false,
      },
    },
    {
      name: 'noteForMeal',
      label: `${t('餐點')}備註`,
      options: {
        display: false,
        filter: false,
        sort: false,
      },
    },
    {
      name: 'note',
      label: '備註',
      options: {
        display: false,
        filter: false,
        sort: false,
      },
    },
    {
      name: 'deliveryStaffFee',
      label: t('送餐費用'),
      type: 'currency',
      options: {
        filter: false,
        sort: true,
        display: true,
      },
    },
    {
      name: 'deliveryStaffFeeCalculationMethod',
      label: '送餐費用計算方法',
      options: {
        filter: false,
        sort: true,
        display: false,
        customBodyRender: (method) => {
          return (deliveryStaffFeeCalculationMethod.find(({ value }) => value === method) || {}).label;
        },
      },
    },
    {
      name: 'mealItems',
      label: t('餐點'),
      options: {
        filter: false,
        sort: true,
        customBodyRender: (mealItems) => {
          return mealItems.map(({ name, quantity, subsidyDayOffset, subsidyTime }) =>
            `${name} x${quantity} ${subsidyDayOffset ? `${subsidyDayOffset}天後${subsidyTime}` : ''}`).join(', ');
        },
        sortCompare(order) {
          return ({ data: value1 }, { data: value2 }) => {
            const mealItem1 = value1[0] || {};
            const mealItem2 = value2[0] || {};
            if (mealItem1.name !== mealItem2.name) {
              return (mealItem1.name > mealItem2.name ? 1 : -1) * (order === 'asc' ? 1 : -1);
            }
            return (mealItem1.quantity > mealItem2.quantity ? 1 : -1) * (order === 'asc' ? 1 : -1);
          };
        },
      },
    },
    {
      name: 'totalCost',
      label: '餐點總進價',
      type: 'currency',
      options: {
        filter: false,
        sort: true,
      },
    },
    {
      name: 'total',
      label: '餐點總金額',
      type: 'currency',
      options: {
        filter: false,
        sort: true,
      },
    },
    {
      name: 'source',
      label: '訂單來源',
      options: {
        display: false,
        filter: false,
        sort: true,
        customBodyRender: (source) => orderSources.find((orderSource) => orderSource.value === source)?.label ?? '',
      },
    },
    {
      name: 'createdBy',
      label: '創立者',
      options: {
        display: false,
        filter: false,
        sort: true,
      },
    },
    {
      name: 'updatedBy',
      label: '更新者',
      options: {
        display: false,
        filter: false,
        sort: true,
      },
    },
    {
      name: 'createdAt',
      label: '創立於',
      type: 'datetime',
      options: {
        filter: false,
        sort: true,
      },
    },
    {
      name: 'updatedAt',
      label: '更新於',
      type: 'datetime',
      options: {
        display: false,
        filter: false,
        sort: true,
      },
    },
    {
      name: 'groupPurchaseDietaryNote',
      label: '團購飲食備註',
      options: {
        display: isEligibleForGroupPurchase || isAdminMode ? true : 'excluded',
        filter: false,
        sort: false,
        customBodyRender: (value) => {
          if (!value) {
            return null;
          }

          const isLong = value.length > 20;

          if (isLong) {
            return (
              <div style={{ minWidth: 160 }}>
                <Typography
                  style={{ cursor: 'pointer', fontSize: 'inherit' }}
                  onClick={() => setDietaryNote((prev) => ({ ...prev, dialogOpen: true, content: value }))}
                  onMouseEnter={(e) => setDietaryNote((prev) => ({ ...prev, popoverAnchorEl: e.currentTarget, content: value }))}
                  onMouseLeave={() => setDietaryNote((prev) => ({ ...prev, popoverAnchorEl: null }))}
                >
                  {`${value.substring(0, 20)}...`}
                </Typography>
              </div>
            );
          }
          return <div style={{ minWidth: 160 }}>{value}</div>;
        },
      },
    },
    {
      name: 'id',
      label: ' ',
      options: {
        display: 'always',
        filter: false,
        sort: false,
        download: false,
        print: false,
        customBodyRender(id) {
          return (
            <OrderDialog id={id} />
          );
        },
      },
    },
    {
      name: 'id',
      label: ' ',
      options: {
        display: 'always',
        filter: false,
        sort: false,
        download: false,
        print: false,
        customBodyRender(id) {
          return (
            <LinkButton
              path={`/order/${id}`}
              label="前往訂單頁面"
            />
          );
        },
      },
    },
    {
      name: 'id',
      label: ' ',
      options: {
        display: isEligibleForReceiptPrinter ? 'always' : 'excluded',
        filter: false,
        sort: false,
        download: false,
        print: false,
        customBodyRender(id, tableMeta) {
          return (
            <ReceiptPrinterButton
              data={[{
                id,
                elder: tableMeta.rowData[columns.findIndex((col) => col.name === 'elder')],
                mealItems: tableMeta.rowData[columns.findIndex((col) => col.name === 'mealItems')],
                deliveryBy: tableMeta.rowData[columns.findIndex((col) => col.name === 'deliveryBy')],
                clientId: tableMeta.rowData[columns.findIndex((col) => col.name === 'clientId')],
                noteForMeal: tableMeta.rowData[columns.findIndex((col) => col.name === 'noteForMeal')],
                deliveryGroupId: tableMeta.rowData[columns.findIndex((col) => col.name === 'deliveryGroupId')],
              }]}
            />
          );
        },
      },
      hideInOrgMode: true,
    },
    {
      name: 'elder',
      label: '',
      options: {
        display: 'excluded',
        filter: false,
        sort: false,
        download: false,
        print: false,
      },
    },
    {
      name: 'clientId',
      label: '',
      options: {
        display: 'excluded',
        filter: false,
        sort: false,
      },
    },
    {
      name: 'deliveryGroupId',
      label: '',
      options: {
        display: 'excluded',
        filter: false,
        sort: false,
      },
    },
  ];

  // const queryFunc = async () => {
  //   if (clientId) {
  //     return asyncListAll(getOrdersByClientByCreatedAt, { clientId });
  //   }

  //   if (elderId) {
  //     return asyncListAll(getOrdersByElderByCreatedAt, { elderId });
  //   }

  //   if (restaurantId) {
  //     return asyncListAll(getOrdersByRestaurantByCreatedAt, { restaurantId });
  //   }

  //   return asyncListAll(listOrders);
  // };

  const updateOrdersByRecurringOrderId = () => {
    if (!recurringOrderId) return;
    setIndexes([]);
    setQueryParams({});
    setData([]);
    (async () => {
      setIsLoading(true);
      try {
        const recurringOrderLogs = await asyncListAll(getRecurringOrderLogByRecurringOrderIdByDate,
          { recurringOrderId, date: { ge: moment().tz(TIME_ZONE).format('YYYY-MM-DD') } });
        const orderIds = recurringOrderLogs.map(({ orderIds }) => orderIds).flat();
        const orders = await Promise.all(orderIds.map(async (id) => {
          const { data: { getOrder: order } } = await request(getOrder, { id });
          return order;
        }));
        setData(orders.filter(({ status }) => status !== 'cancelled'));
      } catch (e) {
        console.error(e);
      }
      setIsLoading(false);
    })();
  };

  const updateOrdersByOrderIds = () => {
    if (!inOrderIds || inOrderIds.length === 0) return;
    setIndexes([]);
    setQueryParams({});
    setData([]);
    (async () => {
      setIsLoading(true);
      try {
        const orders = await Promise.all(inOrderIds.map(async (id) => {
          const { data: { getOrder: order } } = await request(getOrder, { id });
          return order;
        }));
        setData(orders);
      } catch (e) {
        console.error(e);
      }
      setIsLoading(false);
    })();
  };

  useEffect(() => {
    if (deliveryStaffId) {
      const filterClientId = clientId || cache.get('app:facilityId');
      const indexes = [
        {
          name: 'getOrdersByDeliveryStaffByDate',
          label: `按${t('送餐大使')}查詢`,
          partitionKey: 'deliveryStaffId',
          sortKey: 'date',
          fields: [{
            label: `${t('送餐大使')}ID`,
            key: 'deliveryStaffId',
            type: 'select',
            options: [{ value: deliveryStaffId, label: deliveryStaffId }],
            disabled: true,
            size: 6,
          }, {
            label: '狀態',
            key: 'status',
            type: 'select',
            comparisonOperator: 'ne',
            options: [
              { value: 'cancelled', label: '排除取消的訂單' },
              { value: undefined, label: '全部' }],
            size: 6,
          }, {
            label: `${t('送餐')}時間`,
            key: 'date',
            type: 'date',
          }],
          operation: getOrdersByDeliveryStaffByDate,
        },
      ];
      if (filterClientId) {
        indexes[0].fields.push({
          label: '機構',
          key: 'clientId',
          type: 'select',
          options: [{ value: filterClientId, label: filterClientId }],
          disabled: true,
        });
      }
      setIndexes(indexes);
      setQueryParams({
        date: {
          from: lastWeek,
          to: nextWeek,
        },
        deliveryStaffId,
        clientId: filterClientId,
        status: defaultQueryOrderStatus,
      });
    } else
    if (restaurantId) {
      const filterClientId = clientId || cache.get('app:facilityId');
      const indexes = [
        {
          name: 'getOrdersByRestaurantByDate',
          label: `按${t('餐廳')}/送餐時間查詢`,
          partitionKey: 'restaurantId',
          sortKey: 'date',
          fields: [{
            label: `${t('餐廳')} ID`,
            key: 'restaurantId',
            type: 'select',
            options: [{ value: restaurantId, label: restaurantId }],
            disabled: true,
            size: 6,
          }, {
            label: '狀態',
            key: 'status',
            type: 'select',
            comparisonOperator: 'ne',
            options: [
              { value: 'cancelled', label: '排除取消的訂單' },
              { value: undefined, label: '全部' }],
            size: 6,
          }, {
            label: `${t('送餐')}時間`,
            key: 'date',
            type: 'date',
          }],
          operation: getOrdersByRestaurantByDate,
        },
        {
          name: 'getOrdersByRestaurantByCreatedAt',
          label: `按${t('餐廳')}/下單時間查詢`,
          partitionKey: 'restaurantId',
          sortKey: 'createdAt',
          fields: [{
            label: `${t('餐廳')} ID`,
            key: 'restaurantId',
            type: 'select',
            options: [{ value: restaurantId, label: restaurantId }],
            disabled: true,
            size: 6,
          }, {
            label: '狀態',
            key: 'status',
            type: 'select',
            comparisonOperator: 'ne',
            options: [
              { value: 'cancelled', label: '排除取消的訂單' },
              { value: undefined, label: '全部' }],
            size: 6,
          }, {
            label: '下單時間',
            key: 'createdAt',
            type: 'datetime',
          }],
          operation: getOrdersByRestaurantByCreatedAt,
        },
      ];
      if (filterClientId) {
        indexes[0].fields.push({
          label: '機構',
          key: 'clientId',
          type: 'select',
          options: [{ value: filterClientId, label: filterClientId }],
          disabled: true,
        });
        indexes[1].fields.push({
          label: '機構',
          key: 'clientId',
          type: 'select',
          options: [{ value: filterClientId, label: filterClientId }],
          disabled: true,
        });
      }
      setIndexes(indexes);
      setQueryParams({
        date: {
          from: todayDate,
          to: todayDate,
        },
        createdAt: {
          from: todayDate,
          to: todayDate,
        },
        restaurantId,
        clientId: filterClientId,
        status: defaultQueryOrderStatus,
      });
    } else
    if (clientId) {
      setIndexes([
        {
          name: 'getOrdersByClientByDate',
          label: '按客戶查詢',
          partitionKey: 'clientId',
          sortKey: 'date',
          fields: [{
            label: '客戶 ID',
            key: 'clientId',
            type: 'select',
            options: [{ value: clientId, label: clientId }],
            disabled: true,
            size: 6,
          }, {
            label: '狀態',
            key: 'status',
            type: 'select',
            comparisonOperator: 'ne',
            options: [
              { value: 'cancelled', label: '排除取消的訂單' },
              { value: undefined, label: '全部' }],
            size: 6,
          }, {
            label: `${t('送餐')}時間`,
            key: 'date',
            type: 'date',
          }],
          operation: getOrdersByClientByDate,
        },
      ]);
      setQueryParams({
        date: {
          from: todayDate,
          to: todayDate,
        },
        clientId,
        status: defaultQueryOrderStatus,
      });
    } else
    if (elderId) {
      setIndexes([
        {
          name: 'getOrdersByElderByDate',
          label: '按長輩查詢',
          partitionKey: 'elderId',
          sortKey: 'date',
          fields: [{
            label: '長輩 ID',
            key: 'elderId',
            type: 'select',
            options: [{ value: elderId, label: elderId }],
            disabled: true,
            size: 6,
          }, {
            label: '狀態',
            key: 'status',
            type: 'select',
            comparisonOperator: 'ne',
            options: [
              { value: 'cancelled', label: '排除取消的訂單' },
              { value: undefined, label: '全部' }],
            size: 6,
          }, {
            label: `${t('送餐')}時間`,
            key: 'date',
            type: 'date',
          }],
          operation: getOrdersByElderByDate,
        },
      ]);
      setQueryParams({
        date: {
          from: lastWeek,
          to: nextWeek,
        },
        elderId,
        status: defaultQueryOrderStatus,
      });
    } else if (recurringOrderId) {
      updateOrdersByRecurringOrderId();
    } else if (inOrderIds && inOrderIds.length !== 0) {
      updateOrdersByOrderIds();
    } else {
      setIndexes(defaultIndexes);
      setQueryParams({
        date: {
          from: todayDate,
          to: todayDate,
        },
        status: queryStatus,
        county: cache.get('app:location') || counties[0],
      });
    }
  }, [elderId, clientId, restaurantId, recurringOrderId, queryStatus, inOrderIds]);

  useEffect(() => {
    (async () => {
      const deliveryStaffOptions = await getDeliveryStaffIdSchema(null, null, null, true, true);
      setDeliveryStaffOptions(deliveryStaffOptions);
    })();
  }, []);

  const onDataSelectionChange = (dataSelected) => {
    ordersSelected.current = dataSelected;
  };

  const onRefresh = () => {
    if (recurringOrderId) {
      updateOrdersByRecurringOrderId();
    } else if (inOrderIds && inOrderIds.length !== 0) {
      updateOrdersByOrderIds();
    }
  };

  const onParamChange = (params) => {
    if (params.status !== defaultQueryOrderStatus) {
      cache.set('app:query:orderStatus', params.status);
    }
  };

  const customDownloadData = (columns, data) => {
    const tagIndex = columns.findIndex(({ name }) => name === 'elder.tags');
    if (tagIndex !== -1) {
      return data.map((value) => {
        if (value?.data[tagIndex]?.items) {
          value.data[tagIndex] = value.data[tagIndex].items
            .map(({ tag }) => tag)
            .sort(sortBy('name'))
            .sort(sortBy('subcategory'))
            .sort(sortBy('category'))
            .map((tag) => tag.label).join('、');
        }
        return value;
      });
    }
    return data;
  };

  if (!indexes || !queryParams) return null;

  if (isLoading) {
    return (<Loading fullScreen={false} />);
  }

  return (
    <React.Fragment>
      <DataTable
        data={data}
        defaultIndex={defaultIndex}
        indexes={indexes}
        queryDefaultParams={queryParams}
        title={title}
        description={description}
        columns={columns}
        options={options}
        // queryFunc={queryFunc}
        editButton={AdminOrderEditButton}
        selectableRows='multiple'
        onDataSelectionChange={onDataSelectionChange}
        customToolbarSelect={customToolbarSelect}
        onRefresh={indexes && indexes.length !== 0 ? undefined : onRefresh} // 如果有indexes, refresh由DataTable裡的QueryGenerator處理
        customDownloadData={customDownloadData}
        customButton={isEligibleForReceiptPrinter ? ReceiptPrinterButton : undefined}
        onParamChange={onParamChange}
        {...props}
      />
      <Dialog open={dietaryNote.dialogOpen} onClose={() => setDietaryNote((prev) => ({ ...prev, dialogOpen: false, content: '' }))} fullWidth maxWidth="sm">
        <DialogTitle>團購飲食備註</DialogTitle>
        <DialogContent>
          <DialogContentText style={{ whiteSpace: 'pre-wrap' }}>
            {dietaryNote.content}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDietaryNote((prev) => ({ ...prev, dialogOpen: false, content: '' }))} color="primary">
            關閉
          </Button>
        </DialogActions>
      </Dialog>
      <Popover
        className={classes.popover}
        classes={{
          paper: classes.paper,
        }}
        open={isNotePopoverOpen}
        anchorEl={dietaryNote.popoverAnchorEl}
        onClose={() => setDietaryNote((prev) => ({ ...prev, popoverAnchorEl: null }))}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        disableRestoreFocus
      >
        <Typography>{dietaryNote.content}</Typography>
      </Popover>
    </React.Fragment>);
}

OrderTable.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  clientId: PropTypes.string,
  elderId: PropTypes.string,
  deliveryStaffId: PropTypes.string,
  restaurantId: PropTypes.string,
  recurringOrderId: PropTypes.string,
  defaultIndex: PropTypes.number,
  queryStatus: PropTypes.string,
  orderIds: PropTypes.array,
  showElderTags: PropTypes.bool,
};
